import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { date as fmtDate, money, num } from "../lib/format";
import { Icon } from "../lib/icons";
import type { HealthProduct, StockHealth } from "../lib/types";
import { Panel, Skeleton, cn } from "./ui";

// Обзор → «Здоровье запасов»: inventory aging + ABC. Hover a row for its top products, click it for
// the full list (GET /dashboard/segment). Totals and lists come from the same per-product query, so
// they always agree.

const AGING_TONE = ["bg-ok", "bg-accent", "bg-warn", "bg-danger"];
const ABC_TONE: Record<string, string> = { A: "bg-accent", B: "bg-accent/60", C: "bg-accent/30", none: "bg-surface-2" };

type Seg = { kind: "abc" | "aging"; key: string; title: string; count: number };

export function StockHealthCard({ h }: { h: StockHealth }) {
  const [open, setOpen] = useState<Seg | null>(null);
  const agingTotal = h.aging.reduce((a, b) => a + b.stock_value, 0) || 1;
  const abcShipTotal = h.abc.reduce((a, b) => a + b.shipment_value, 0) || 1;

  return (
    <Panel
      title="Здоровье запасов"
      actions={
        <span className="text-xs text-text-2">
          <span className="hidden sm:inline">наведите на строку — товары · клик — весь список</span>
          <span className="sm:hidden">нажмите на строку — весь список</span>
        </span>
      }
    >
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* ---------- aging ---------- */}
        <div>
          <div className="mb-1 text-sm font-semibold">Возраст запасов</div>
          <p className="mb-3 text-xs text-text-2">Сколько дней товар лежит без отгрузки</p>
          <div className="mb-4 flex h-2.5 overflow-hidden rounded-full bg-surface-2">
            {h.aging.map((a, i) => (
              <span key={a.key} className={AGING_TONE[i]} style={{ width: `${(a.stock_value / agingTotal) * 100}%` }} />
            ))}
          </div>
          <div className="divide-y divide-border">
            {h.aging.map((a, i) => (
              <SegRow
                key={a.key}
                items={a.items}
                count={a.products}
                mode="aging"
                onOpen={() => setOpen({ kind: "aging", key: a.key, title: `Возраст запасов: ${a.bucket}`, count: a.products })}
              >
                <span className={cn("h-2.5 w-2.5 flex-none rounded-sm", AGING_TONE[i])} />
                <span className="min-w-0 flex-1 sm:flex sm:items-center">
                  <span className="block font-medium sm:w-20 sm:flex-none">{a.bucket}</span>
                  <span className="block text-xs text-text-2 tnum sm:text-sm">{num(a.products)} тов.</span>
                </span>
                <span className="flex-none text-right">
                  <span className="block font-semibold tnum">{money(a.stock_value)}</span>
                  <span className="block text-xs text-text-2 tnum sm:hidden">{Math.round((a.stock_value / agingTotal) * 100)}%</span>
                </span>
                <span className="hidden w-11 flex-none text-right text-xs text-text-2 tnum sm:block">
                  {Math.round((a.stock_value / agingTotal) * 100)}%
                </span>
              </SegRow>
            ))}
          </div>
          <HowCalculated>
            Для каждого товара с остатком &gt; 0 берём дату последней отгрузки (если товар ни разу не
            отгружался — дату первой поставки) и считаем, сколько дней прошло до последнего дня в данных.
            Товары распределяются по интервалам 0–30, 31–90, 91–180 и 180+ дней; сумма — «Сумма остатка»
            товара. Чем больше денег в правых интервалах, тем больше «замороженного» капитала.
          </HowCalculated>
        </div>

        {/* ---------- ABC ---------- */}
        <div>
          <div className="mb-1 text-sm font-semibold">ABC-анализ</div>
          <p className="mb-3 text-xs text-text-2">По сумме отгрузок за 90 дней: A — 80%, B — 15%, C — 5%</p>
          <div className="mb-4 flex h-2.5 overflow-hidden rounded-full bg-surface-2">
            {h.abc.map((a) => (
              <span key={a.class} className={ABC_TONE[a.class]} style={{ width: `${(a.shipment_value / abcShipTotal) * 100}%` }} />
            ))}
          </div>
          <div className="divide-y divide-border">
            {h.abc.map((a) => (
              <SegRow
                key={a.class}
                items={a.items}
                count={a.products}
                mode="abc"
                onOpen={() => setOpen({ kind: "abc", key: a.class, title: `ABC-анализ: класс ${a.class}`, count: a.products })}
              >
                <span className={cn("grid h-6 w-6 flex-none place-items-center rounded-md text-2xs font-bold text-white", ABC_TONE[a.class])}>
                  {a.class}
                </span>
                <span className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between sm:gap-3">
                  <span className="block text-text-2 tnum">{num(a.products)} тов.</span>
                  <span className="block text-xs text-text-2 tnum">{Math.round((a.shipment_value / abcShipTotal) * 100)}% отгрузок</span>
                </span>
                <span className="flex-none text-right font-semibold tnum sm:min-w-[132px]" title="Сумма остатка этих товаров на складе">
                  {money(a.stock_value)}
                </span>
              </SegRow>
            ))}
            <SegRow
              items={h.abc_none.items}
              count={h.abc_none.products}
              mode="none"
              onOpen={() => setOpen({ kind: "abc", key: "none", title: "Без отгрузок за 90 дней", count: h.abc_none.products })}
            >
              <span className="grid h-6 w-6 flex-none place-items-center rounded-md bg-surface-2 text-2xs font-bold text-text-2">—</span>
              <span className="flex-1 text-text-2 tnum">{num(h.abc_none.products)} тов. без отгрузок</span>
              <span className="flex-none text-right font-semibold text-warn tnum sm:min-w-[132px]" title="Сумма остатка товаров без отгрузок за 90 дней">
                {money(h.abc_none.stock_value)}
              </span>
            </SegRow>
          </div>
          <HowCalculated>
            Берём сумму отгрузок каждого товара за последние 90 дней и сортируем товары от большей суммы к
            меньшей. Идём по списку, накапливая долю от общей суммы отгрузок: пока накоплено меньше 80% —
            товар в классе A, от 80% до 95% — B, остальные — C. Справа — «Сумма остатка» этих товаров на
            складе сейчас. «—» — товары с остатком, у которых за 90 дней не было ни одной отгрузки.
          </HowCalculated>
        </div>
      </div>

      {open && <SegmentModal seg={open} onClose={() => setOpen(null)} />}
    </Panel>
  );
}

/** One class / bucket row: hover shows its top products, click (or Enter) opens the full list. */
function SegRow({
  items,
  count,
  mode,
  onOpen,
  children,
}: {
  items: HealthProduct[];
  count: number;
  mode: "abc" | "aging" | "none";
  onOpen: () => void;
  children: ReactNode;
}) {
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onOpen}
        disabled={count === 0}
        className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-md px-2 py-2 text-left text-sm hover:bg-surface-3 disabled:cursor-default"
      >
        {children}
        <Icon name="chevron" size={14} className="flex-none text-muted opacity-0 group-hover:opacity-100" />
      </button>
      {items.length > 0 && (
        <div className="pointer-events-none absolute left-0 right-0 top-full z-30 hidden pt-1 group-hover:block">
          <div className="rounded-lg border border-border bg-surface p-2 shadow-pop">
            <div className="px-2 pb-1.5 pt-0.5 text-2xs font-semibold uppercase tracking-wider text-text-2">
              {mode === "abc" ? "Главные по сумме отгрузок" : "Крупнейшие по сумме остатка"}
            </div>
            {items.map((p) => (
              <div key={p.product_id} className="flex items-center gap-3 rounded-md px-2 py-1.5 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{p.product_name}</div>
                  <div className="truncate text-xs text-text-2">
                    {[p.brand, p.category].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="flex-none text-right tnum">
                  <div className="font-semibold">
                    {mode === "abc" ? money(p.shipment_value) : money(p.stock_value)}
                  </div>
                  <div className="text-xs text-text-2">
                    {mode === "abc"
                      ? `${((p.share ?? 0) * 100).toFixed(1)}% · остаток ${num(p.stock)}`
                      : mode === "aging"
                        ? `${num(p.days_idle ?? 0)} дн без отгрузки`
                        : `остаток ${num(p.stock)} шт`}
                  </div>
                </div>
              </div>
            ))}
            {count > items.length && (
              <div className="border-t border-border px-2 pb-0.5 pt-1.5 text-xs text-accent">
                Нажмите, чтобы увидеть все {num(count)} товаров
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function HowCalculated({ children }: { children: ReactNode }) {
  return (
    <details className="group/how mt-3 rounded-lg border border-border bg-surface-3 px-3 py-2 text-sm">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 font-medium text-text-2 hover:text-text">
        <Icon name="info" size={14} />
        Как считается?
        <Icon name="chevron" size={14} className="ml-auto transition-transform group-open/how:rotate-90" />
      </summary>
      <p className="mt-2 leading-relaxed text-text-2">{children}</p>
    </details>
  );
}

/** Full product list of one segment, searchable; a row opens the product card. */
function SegmentModal({ seg, onClose }: { seg: Seg; onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const { data, isLoading, error } = useQuery({
    queryKey: ["segment", seg.kind, seg.key],
    queryFn: () => api.get<HealthProduct[]>("/dashboard/segment", { kind: seg.kind, key: seg.key }).then((r) => r.data),
  });
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    const all = data ?? [];
    return t
      ? all.filter((p) => [p.product_name, p.product_id, p.brand, p.category].some((v) => String(v ?? "").toLowerCase().includes(t)))
      : all;
  }, [data, q]);
  const isAbc = seg.kind === "abc" && seg.key !== "none";
  const isAging = seg.kind === "aging";

  return (
    <div className="fixed inset-0 z-[85] grid place-items-center bg-black/30 p-4 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-pop animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0 flex-1">
            <h4 className="truncate text-md font-semibold">{seg.title}</h4>
            <div className="text-sm text-text-2">
              {num(seg.count)} товаров · {isAbc ? "по сумме отгрузок за 90 дней" : "по сумме остатка"}
            </div>
          </div>
          <button type="button" onClick={onClose} title="Закрыть" className="grid h-8 w-8 flex-none place-items-center rounded-lg text-text-2 hover:bg-surface-2 hover:text-text">
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="border-b border-border px-5 py-3">
          <div className="relative">
            <Icon name="search" size={16} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Найти товар, SKU, бренд…"
              className="min-h-9 w-full rounded-lg border border-border bg-surface py-1.5 pl-8 pr-3 text-sm focus:border-accent/60 focus:shadow-[0_0_0_3px_rgb(var(--accent)/0.12)] focus:outline-none"
            />
          </div>
        </div>
        <div className="scroll-thin min-h-0 flex-1 overflow-auto">
          {isLoading ? (
            <div className="space-y-2 p-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : error ? (
            <div className="p-8 text-center text-sm text-danger">Не удалось загрузить список</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-text-2">Ничего не найдено</div>
          ) : (
            <table className="w-full table-fixed text-sm">
              <thead className="sticky top-0 z-[1] bg-surface-3 text-left text-xs text-text-2">
                <tr>
                  <th className="px-5 py-2 font-semibold">Товар</th>
                  {isAbc && <th className="w-[160px] px-3 py-2 text-right font-semibold">Отгрузки 90 дн</th>}
                  {isAging && <th className="w-[140px] px-3 py-2 text-right font-semibold">Без отгрузки</th>}
                  <th className="w-[90px] px-3 py-2 text-right font-semibold">Остаток</th>
                  <th className="w-[170px] px-5 py-2 text-right font-semibold">Сумма остатка</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((p) => (
                  <tr
                    key={p.product_id}
                    onClick={() => navigate(`/product/${encodeURIComponent(p.product_id)}`)}
                    className="cursor-pointer hover:bg-surface-3"
                  >
                    <td className="px-5 py-2">
                      <div className="truncate font-medium">{p.product_name}</div>
                      <div className="truncate text-xs text-text-2">
                        {[p.product_id, p.brand, p.category].filter(Boolean).join(" · ")}
                      </div>
                    </td>
                    {isAbc && (
                      <td className="whitespace-nowrap px-3 py-2 text-right tnum">
                        <div className="font-medium">{money(p.shipment_value)}</div>
                        <div className="text-xs text-text-2">{((p.share ?? 0) * 100).toFixed(1)}% · {num(p.shipped_qty)} шт</div>
                      </td>
                    )}
                    {isAging && (
                      <td className="whitespace-nowrap px-3 py-2 text-right tnum">
                        <div className="font-medium">{num(p.days_idle ?? 0)} дн</div>
                        <div className="text-xs text-text-2">{p.last_ship ? `посл. ${fmtDate(p.last_ship)}` : "не отгружался"}</div>
                      </td>
                    )}
                    <td className="whitespace-nowrap px-3 py-2 text-right tnum">{num(p.stock)}</td>
                    <td className="whitespace-nowrap px-5 py-2 text-right font-semibold tnum">{money(p.stock_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
